import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Result, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { Command } from 'pos/common';
import * as SalesStore from '../SalesStore';
import { Permission, Res, TipDTO, TicketType } from 'core/api';
import { Message, Modal } from 'core/ui-next/modal';

type Props = { initialTip: TipDTO } & Modal.Actions<TipDTO>;


class EditTipSettingsScreen extends PosScreenClassComponent<Props, TipDTO> {
	private form = React.createRef<UI.Form<TipDTO>>();

	protected getScreenId(): string {
		return 'EDIT_TIP_SETTINGS';
	}

	protected getTitle(): string {
		return Res.Web.EDIT_TICKET_TIP_TEXT();
	}

	protected getImage(): string | SvgIcon {
		return Images.TipSettings;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.okCancelButtons({ onOK: this.onOk });
	}

	private onOk = async () => {
		const updated = await this.form.current!.updateModel();
		if (updated) {
			this.close(this.state);
		}

	};

	protected getContent(): React.ReactNode {
		const { Form, DecimalBox, ComboBox, CheckBox } = UI.controlsFor<TipDTO>();

		return <Form small className='m-t-md' ref={this.form} model={this.state}>
			<DecimalBox label={Res.Web.EDIT_TICKET_TIP_TIP_PERCENTAGE_LBL()}
				linkTo={x => x.TipPercentage}
				allowNegative={false} precision={2}
				rules={UI.Constraints.range(0, 100)}>
				<span className='input-group-addon' style={{ width: 35 }}>%</span>
			</DecimalBox>
			<ComboBox label={Res.Web.EDIT_TICKET_TIP_APPLY_TO_LBL()}
				linkTo={x => x.ApplyTo}
				options={this.props.initialTip.ExistingTicketType}
				format={TicketType.toString}
				required />
			<CheckBox label={Res.Web.EDIT_TICKET_TICKET_IGNORE_TICKET_DISCOUNTS()}
				linkTo={x => x.IgnoreTicketDiscounts} />
			<ComboBox label={Res.Web.EDIT_TICKET_TIP_TIP_VAT_LBL()}
				linkTo={x => x.TipTax}
				options={this.props.initialTip.ExistingTaxes} />
		</Form>;

	}

	createInitialState(props: Props): Nullable<TipDTO> {
		return { ...props.initialTip };
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}
}

declare global {
	interface CommandType {
		editTipSettings: () => Promise<Result>
	}
}

Command.register('editTipSettings', Preconditions.ensure(Permission.Ticket_EditTip), async (): Promise<Result> => {

	if (!SessionProvider.allowFixedTipSettings()) {
		await Message.show(Res.Msg.MSG_CANNOT_SET_FIXED_TIP_SETTINGS());
		return Result.error();
	}

	const existingTip = await SalesStore.getTipSettings();
	if (existingTip === null)
		return Result.error();

	const tipResult = await Modal.show(EditTipSettingsScreen, { initialTip: existingTip });
	if (!tipResult.ok)
		return Result.error();

	await SalesStore.setTipSettings(tipResult.value);
	return Result.ok();
});